import { handleHttpError } from '@/utils/errorHandlers';
import {
  type ArcualNotification,
  getNotificationService,
  type HttpError,
  type ListFilters,
  type ResultList,
} from '@arcual/lib-app-platform';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';
import { useAccountStore } from '../AccountStore/AccountStore';

export const NotificationStoreName = 'NotificationStore';
export const useNotificationStore = defineStore(NotificationStoreName, () => {
  const hasUnreadMessages = ref(false);
  const items: Ref<Map<number, Array<ArcualNotification>>> = ref<Map<number, Array<ArcualNotification>>>(new Map());
  const total = ref(0);

  const _resultSetRequest = async (
    requestFunction: () => Promise<ResultList<ArcualNotification>>,
    filters: ListFilters,
  ) => {
    const resultList = await requestFunction().catch((error: HttpError) => {
      handleHttpError(error);
      return { total: 0, items: [] };
    });

    total.value = resultList.total;
    items.value.set(filters.page!, resultList.items);

    return resultList;
  };

  const clearUnreadMessages = () => {
    hasUnreadMessages.value = false;
  };

  const fetchNotifications = async (
    filters: ListFilters,
    options: { skipCaching?: boolean; resetCache?: boolean } = {},
  ): Promise<ResultList<ArcualNotification>> => {
    const { skipCaching = false, resetCache = false } = options;
    await checkUnreadNotifications();
    // if there's now new messages, reset the cache
    // todo: ideally we should only reset the cache for the first page and move the rest down the queue
    if (hasUnreadMessages.value === true || resetCache) {
      items.value.clear();
    }
    // check if we have the page on cache and return it
    if (items.value.has(filters.page!)) {
      return _fetchCachedNotifications(filters);
    }

    // otherwise, return cached items
    const notifications = getNotificationService().getNotifications(filters);

    if (skipCaching) {
      return notifications;
    }

    await _resultSetRequest(() => notifications, filters);

    return _fetchCachedNotifications(filters);
  };

  const _fetchCachedNotifications = (filters: ListFilters): ResultList<ArcualNotification> => {
    return {
      items: items.value.get(filters.page!) || [],
      total: total.value,
    };
  };

  const checkUnreadNotifications = async (setupSSE = true) => {
    const accountId = useAccountStore().currentAccount().value?.id;
    if (!accountId) {
      return;
    }
    const hasUnread = await getNotificationService().checkUnreadNotifications();
    hasUnreadMessages.value = hasUnread.hasNew;
    if (setupSSE) {
      // await setupSseListener(accountId);
    }
  };

  const markAsChecked = async (payload: ArcualNotification) => {
    _updateNotification({
      ...payload,
      checkedAt: new Date().toISOString(),
    });
    await getNotificationService().markAsChecked({
      ids: [
        {
          id: payload.id,
          rawEventId: payload.rawEvent.id,
        },
      ],
    });
  };

  const _updateNotification = (notification: ArcualNotification) => {
    const pages = Array.from(items.value.keys());
    for (const page of pages) {
      const pageItems = items.value.get(page);
      if (pageItems) {
        const index = pageItems.findIndex((n) => n.id === notification.id);

        if (index !== -1) {
          pageItems[index] = notification;
        }
      }
    }
  };

  const setLastSeen = async () => {
    return getNotificationService().setLastSeen(new Date().toISOString());
  };

  const reset = () => {
    clearUnreadMessages();
  };

  return {
    hasUnreadMessages,
    fetchNotifications,
    clearUnreadMessages,
    checkUnreadNotifications,
    markAsChecked,
    setLastSeen,
    reset,
  };
});
